import {
  getIncentivesByPlatformIdReferralRewardsByPersonIdRedeemable,
  getIncentivesByPlatformLocationIdActiveSignupReward,
  getIncentivesByPlatformLocationIdAvailableReferralReward,
  getPlatform,
  getRewards,
  getUser,
} from "api/methods";
import { STALE_TIME } from "api/staleTimes";
import { prefetchContent } from "modules/routing/contentQueries";
import { queryClient } from "pages/Root";

const getAwardedReferralRewards = getIncentivesByPlatformIdReferralRewardsByPersonIdRedeemable;

const prefetchReferralRewards = async (
  platformId: string,
  personId: string,
  platformLocationId: string,
) => {
  await Promise.allSettled([
    queryClient.prefetchQuery(awardedReferralRewardsQuery(platformId, personId)),
    queryClient.prefetchQuery({
      ...getIncentivesByPlatformLocationIdAvailableReferralReward.keyed({ platformLocationId }),
    }),
    queryClient.prefetchQuery({
      ...getIncentivesByPlatformLocationIdActiveSignupReward.keyed({ platformLocationId }),
    }),
  ]);
};

/**
 * The rewards page's own data, as individual queries the page reads through `useQuery` rather than
 * one composite the route waits for.
 */
export const rewardsContentQueries = {
  platform: () => ({ ...getPlatform.keyed(), staleTime: STALE_TIME.platformConfig }),
  userInfo: () => ({ ...getUser.keyed(), staleTime: 1000 * 60 * 2 }),
  rewards: () => ({ ...getRewards.keyed({}) }),
} as const;

/**
 * Redeemable referral rewards are keyed by both the practice and the patient, so this one cannot
 * start until those two have landed.
 */
export const awardedReferralRewardsQuery = (platformId: string, personId: string) => ({
  queryKey: getAwardedReferralRewards.key({ platformId, personId }),
  queryFn: () => getAwardedReferralRewards({ platformId, personId }),
});

export const prefetchRewardsIndex = () => {
  const content = prefetchContent(rewardsContentQueries);

  void (async () => {
    const [platform, userInfo] = await Promise.all([
      queryClient.fetchQuery(rewardsContentQueries.platform()),
      queryClient.fetchQuery(rewardsContentQueries.userInfo()),
    ]);
    const platformLocationId = userInfo?.platformLocation?.id ?? "";

    await prefetchReferralRewards(platform?.id ?? "", userInfo.id, platformLocationId);
  })().catch(() => undefined);

  return content;
};

export type RewardsContentData = {
  platform: NonNullable<Awaited<ReturnType<typeof getPlatform>>>;
  userInfo: Awaited<ReturnType<typeof getUser>>;
  rewards: Awaited<ReturnType<typeof getRewards>>;
  awardedReferralRewards: Awaited<ReturnType<typeof getAwardedReferralRewards>> | null;
};
